import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { X, MessageSquare, AtSign, Calendar, AlertCircle, UserPlus, CheckSquare, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const NOTIFICATION_ICONS = {
  mention: AtSign,
  comment: MessageSquare,
  reply: MessageSquare,
  assignment: CheckSquare,
  expiry_warning: AlertCircle,
  status_change: Calendar,
  invitation: UserPlus,
  check_in: CheckSquare,
  approval: CheckSquare,
  team_update: UserPlus,
  system: AlertCircle,
  leave: Plane,
  alert: AlertCircle,
};

export const NotificationItem = ({ notification, onMarkAsRead, onDelete, onClose }) => {
  const navigate = useNavigate();
  const Icon = NOTIFICATION_ICONS[notification.type] || MessageSquare;

  const handleClick = () => {
    // Mark as read if unread
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    // Navigate to link if provided
    if (notification.link_url) {
      navigate(notification.link_url);
      onClose?.();
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  return (
    <div
      className={`group relative px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
        !notification.is_read ? 'bg-blue-50/50' : ''
      }`}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-blue-600 rounded-full" />
      )}

      <div className="flex gap-3">
        {/* Avatar/Icon */}
        {notification.actor ? (
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarImage src={notification.actor.avatar_url} />
            <AvatarFallback>
              {notification.actor.first_name?.[0]}
              {notification.actor.last_name?.[0]}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center">
            <Icon className="h-5 w-5 text-gray-600" />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${!notification.is_read ? 'font-semibold' : 'font-medium'} text-gray-900`}>
            {notification.title}
          </p>
          {notification.message && (
            <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
              {notification.message}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            {formatDistanceToNow(new Date(notification.created_date), { addSuffix: true })}
          </p>
        </div>

        {/* Delete button */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          onClick={handleDelete}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
